/**
 * ProjectLogs Component
 * 
 * This component displays project-specific timesheets/logs in the Project Details page.
 * It's a project-scoped version of the All-Timesheets module.
 * 
 * @param {string} projectId - The ID of the project
 * @param {string} projectName - The name of the project
 */

import { useState } from 'react';
import { Button } from 'react-bootstrap';

import {
    AllTimesheetsTable,
    Pagination
} from '../../../../components/MyAllTimesheets/components';

import {
    priorityOptions,
    assigneeOptions,
    taskListOptions,
    stageOptions
} from '../../../../components/MyTasks/constants/options';

import { AddProjectTaskModal } from './ProjectLogPopup';
import FilterProjectLogsOffcanvas from './ProjectLogsFilter';

const ProjectLogs = ({ projectId, projectName }) => {
    const [show, setShow] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showAddTask, setShowAddTask] = useState(false); // Add Task Modal

    // ==================== Task Form States ====================
    const [taskName, setTaskName] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [estimatedTime, setEstimatedTime] = useState('');
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [description, setDescription] = useState('');
    const [notifyByEmail, setNotifyByEmail] = useState(false);

    // ==================== Filter States ====================
    const [taskNameFilter, setTaskNameFilter] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [assignToFilter, setAssignToFilter] = useState([]);
    const [startDateFilter, setStartDateFilter] = useState(null);
    const [endDateFilter, setEndDateFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [projectStatusFilter, setProjectStatusFilter] = useState(null);

    // Handler functions
    const handleShow = () => setShow(true);
    
    // Task Modal Handlers
    const handleAddTaskShow = () => setShowAddTask(true);
    const handleAddTaskClose = () => {
        setShowAddTask(false);
        resetTaskForm();
    };

    const resetTaskForm = () => {
        setTaskName('');
        setSelectedDepartment(null);
        setStartDate(new Date());
        setEndDate(new Date());
        setEstimatedTime('');
        setSelectedPriority(null);
        setSelectedAssignees([]);
        setDescription('');
        setNotifyByEmail(false);
    };

    const handleTaskSubmit = () => {
        const taskData = {
            projectId,
            taskName,
            department: selectedDepartment,
            startDate,
            endDate,
            estimatedTime,
            priority: selectedPriority,
            assignees: selectedAssignees,
            description,
            notifyByEmail
        };
        console.log('Creating task for project:', projectId, taskData);
        // TODO: API call to create task
        // TODO: Refresh logs list
    };

    // Filter Handlers
    const applyFilters = () => {
        console.log('Applying filters for project:', projectId, {
            taskName: taskNameFilter,
            department: departmentFilter,
            assignTo: assignToFilter,
            startDate: startDateFilter,
            endDate: endDateFilter,
            priority: priorityFilter,
            projectStatus: projectStatusFilter
        });
        // TODO: Implement filter logic
    };

    const clearFilters = () => {
        setTaskNameFilter('');
        setDepartmentFilter(null);
        setAssignToFilter([]);
        setStartDateFilter(null);
        setEndDateFilter(null);
        setPriorityFilter(null);
        setProjectStatusFilter(null);
        // TODO: Refresh logs list
    };
    const handleMoveOrCopyShow = () => {
        // TODO: Implement move/copy functionality
        console.log('Move/Copy show for project:', projectId);
    };
    const handleLogTimeShow = (data) => {
        // TODO: Implement log time functionality
        console.log('Log time for project:', projectId, data);
    };
    const handleHistoryShow = () => {
        // TODO: Implement history view
        console.log('Show history for project:', projectId);
    };
    const handleQuickSubtasksShow = () => {
        // TODO: Implement quick subtasks
        console.log('Show quick subtasks for project:', projectId);
    };
    const handleDeleteShow = () => {
        // TODO: Implement delete functionality
        console.log('Show delete confirmation for project:', projectId);
    };
    const handleEstimateTimeShow = () => {
        // TODO: Implement time estimation
        console.log('Show time estimation for project:', projectId);
    };

    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const totalItems = 100; // This would come from your API filtered by projectId
    const totalPages = Math.ceil(totalItems / pageSize);

    return (
        <>
            <div className='c-card-1'>
                <div className='c-card-header-1'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <div> Timesheet - {projectName} </div>
                        <div className='d-flex align-items-center gap-1'>
                            <Button variant='outline-primary' 
                                className='btn-sm me-2 text-nowrap'
                                onClick={() => setShowFilter(true)}
                            >
                                <i className="fas fa-filter me-1"></i>
                                Filter
                            </Button>
                            <Button variant='primary'
                                className='btn-sm text-nowrap'
                                onClick={handleAddTaskShow}
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add Logs
                            </Button>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1 p-0'>
                    <AllTimesheetsTable
                        onEdit={handleShow}
                        onMoveOrCopy={handleMoveOrCopyShow}
                        onLogTime={handleLogTimeShow}
                        onViewHistory={handleHistoryShow}
                        onAddSubtasks={handleQuickSubtasksShow}
                        onView={() => {}} // TODO: Implement view handler
                        onDuplicate={() => {}} // TODO: Implement duplicate handler
                        onDelete={handleDeleteShow}
                        onEstimateTime={handleEstimateTimeShow}
                    />
                </div>
                <div className='c-card-footer-1'>
                    <Pagination
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                    />
                </div>
            </div>

            {/* Project Logs Filter Offcanvas - Custom Filter */}
            <FilterProjectLogsOffcanvas
                show={showFilter}
                onHide={() => setShowFilter(false)}
                taskNameFilter={taskNameFilter}
                setTaskNameFilter={setTaskNameFilter}
                departmentFilter={departmentFilter}
                setDepartmentFilter={setDepartmentFilter}
                assignToFilter={assignToFilter}
                setAssignToFilter={setAssignToFilter}
                startDateFilter={startDateFilter}
                setStartDateFilter={setStartDateFilter}
                endDateFilter={endDateFilter}
                setEndDateFilter={setEndDateFilter}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                projectStatusFilter={projectStatusFilter}
                setProjectStatusFilter={setProjectStatusFilter}
                departmentOptions={taskListOptions}
                assignToOptions={assigneeOptions}
                priorityOptions={priorityOptions}
                projectStatusOptions={stageOptions}
                onApply={applyFilters}
                onClear={clearFilters}
            />

            {/* Add Task Modal - Modified for Project Tasks */}
            <AddProjectTaskModal
                show={showAddTask}
                onHide={handleAddTaskClose}
                taskName={taskName}
                setTaskName={setTaskName}
                selectedDepartment={selectedDepartment}
                setSelectedDepartment={setSelectedDepartment}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                estimatedTime={estimatedTime}
                setEstimatedTime={setEstimatedTime}
                selectedPriority={selectedPriority}
                setSelectedPriority={setSelectedPriority}
                selectedAssignees={selectedAssignees}
                setSelectedAssignees={setSelectedAssignees}
                description={description}
                setDescription={setDescription}
                notifyByEmail={notifyByEmail}
                setNotifyByEmail={setNotifyByEmail}
                departmentOptions={taskListOptions}
                priorityOptions={priorityOptions}
                assigneeOptions={assigneeOptions}
                onSubmit={handleTaskSubmit}
            />
        </>
    );
};

export default ProjectLogs;
